import { AnyAction, Reducer } from 'redux';

import actionTypes from '../types/authTypes';
import { updateObject } from '../../utils/utility';
import { AuthStart, AuthState, AuthSuccess, AuthError } from '../models/authModel';
import Cookies from 'js-cookie';

const initialState: AuthState = {
  token: Cookies.get('r2_dto'),
  error: null,
  loading: false,
  authRedirectPath: '/',
  isPasswordExpired: false,
  isFreshBotInit: false,
  surveyQuestions: [],
  userSurveyDetails: {},
  surveyResp: {},
  userPerformance: {},
  encryptedTokenAuth: '',
  userName: '',
  password: '',
  otpFlowLoading: false,
  deeplinkDto: null,
  waitTimeToResendOTPInSec: 0,
  isOTPRequestLimitExhausted: false,
};

const authStart = (state: AuthState): AuthState =>
  updateObject<AuthState, AuthStart>(state, { error: null, loading: true });

const clearErrorMessage = (state: AuthState): AuthState => updateObject<AuthState, AuthError>(state, { error: null });

const authSuccess = (state: AuthState, { payload: { token } }: AnyAction): AuthState =>
  updateObject<AuthState, AuthSuccess>(state, { token, error: null, loading: false });

const authFail = (state: AuthState, { payload: error }: AnyAction): AuthState =>
  updateObject(state, { error, loading: false, otpFlowLoading: false });

const authDetails = (state: AuthState, { payload }: AnyAction): AuthState =>
  updateObject(state, { userName: payload?.userName, password: payload?.password });

const authLogout = (state: AuthState): AuthState =>
  updateObject(state, { token: null });

const setAuthRedirectPath = (state: AuthState, { payload }: AnyAction): AuthState =>
  updateObject(state, { authRedirectPath: payload });

const authPasswordExpired = (state: AuthState, { payload }: AnyAction): AuthState =>
  updateObject(state, { isPasswordExpired: payload, loading: false, otpFlowLoading: false });

const setEncryptedToken = (state: AuthState, { payload }: AnyAction): AuthState =>
  updateObject(state, { encryptedTokenAuth: payload });

const setOtpTimerLimit = (state: AuthState, { payload }: AnyAction): AuthState =>
  updateObject(state, { waitTimeToResendOTPInSec: payload });

const setIsOtpLimitExhausted = (state: AuthState, { payload }: AnyAction): AuthState =>
  updateObject(state, { isOTPRequestLimitExhausted: payload });

const setFreshBotInit = (state: AuthState, { payload }: AnyAction): AuthState =>
  updateObject(state, { isFreshBotInit: payload });

const surveySuccess = (state: AuthState, { payload }: AnyAction): AuthState =>
  updateObject(
    state,
    {
      surveyQuestions: payload?.surveyQuestions || [],
      userSurveyDetails: payload?.userSurveyDetails || {},
      surveyResp: initialState.surveyResp,
    },
  );

const saveSurveySuccess = (state: AuthState, { payload }: AnyAction): AuthState =>
  updateObject(state, {surveyResp: payload});

const getUserPerformanceSuccessRed = (state: AuthState, { payload }: AnyAction): AuthState =>
  updateObject(state, {userPerformance: payload});

const setOtpFlowLoding = (state: AuthState, { payload }: AnyAction): AuthState =>
  updateObject(
    state,
    {
      otpFlowLoading: payload,
    },
  );

const setDeepLinkDto = (state: AuthState, { payload }: AnyAction): AuthState =>
  updateObject(
    state,
    {
      deeplinkDto: payload,
    },
  );

const reducer: Reducer<AuthState> = (
  state: AuthState = initialState,
  action: AnyAction,
): AuthState | any => {
  switch (action.type) {
    case actionTypes.AUTH_START: return authStart(state);
    case actionTypes.AUTH_SUCCESS: return authSuccess(state, action);
    case actionTypes.AUTH_FAIL: return authFail(state, action);
    case actionTypes.AUTH_DETAILS: return authDetails(state, action);
    case actionTypes.CLEAR_AUTH_ERROR: return clearErrorMessage(state);
    case actionTypes.AUTH_LOGOUT: return authLogout(state);
    case actionTypes.SET_AUTH_REDIRECT_PATH: return setAuthRedirectPath(state, action);
    case actionTypes.AUTH_PASSWORD_EXPIRED: return authPasswordExpired(state, action);
    case actionTypes.SET_ENCRYPTED_TOKEN: return setEncryptedToken(state, action);
    case actionTypes.SET_FRESH_BOT_INIT: return setFreshBotInit(state, action);
    case actionTypes.GET_SURVEY_OPTIONS_SUCCESS: return surveySuccess(state, action);
    case actionTypes.SAVE_SURVEY_SUCCESS: return saveSurveySuccess(state, action);
    case actionTypes.GET_USER_PERFORMANCE_SUCCESS: return getUserPerformanceSuccessRed(state, action);
    case actionTypes.SET_OTP_FLOW_LOADER: return setOtpFlowLoding(state, action);
    case actionTypes.SET_DEEPLINK_DTO: return setDeepLinkDto(state, action);
    case actionTypes.SET_OTP_TIMER_LIMIT: return setOtpTimerLimit(state, action);
    case actionTypes.SET_IS_OTP_REQUEST_LIMIT_EXHAUSTED: return setIsOtpLimitExhausted(state, action);
    default: return state;
  }
};

export { reducer as authReducer };
